// BookingManagePage — /bookings/manage/:token
//
// The self-serve surface behind "Reschedule or cancel" in booking confirmation
// and reminder emails. The signed token in the URL is the only auth (it binds
// booking id + email server-side); same forwarded-link posture as the
// preference centre.
//
// Reschedule reuses NativeBookingPicker in rebook mode — the engine already
// knows who this is, so it's pick → confirm, no email/names steps. The server
// creates the NEW booking fully (Zoom, calendar, fresh confirmation email)
// before releasing the old one, so the booker can never end up with nothing.
//
// A call that has started reads as on (join link only), and one whose time has
// passed or that was cancelled reads as such, with one "Book a new time" button
// on the same booking type and no join, reschedule or cancel (mct_2057e272-bf2).
// The server decides the phase (booking-join-core.ts); the page only renders it.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { CSSProperties } from 'react';
import TakerShell from './components/TakerShell';
import { NativeBookingPicker } from './components/NativeBookingPicker';
import { fetchWithRetry } from './lib/fetchRetry';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface ManageData {
  booking: {
    id: string;
    status: string;
    superseded: boolean;
    /** Absent from a worker older than this page: treated as 'upcoming'. */
    phase?: 'upcoming' | 'live' | 'past' | 'cancelled';
    startIso: string;
    durationMinutes: number | null;
    bookerTimezone: string | null;
    joinUrl: string | null;
    firstName: string | null;
    hostName: string | null;
  };
  rebook: { personSlug: string; bookingKey: string } | null;
  /** Past or cancelled only: a fresh booking on the same host and type. */
  bookAgainUrl?: string | null;
}

function whenLabel(iso: string, tz: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz ?? undefined,
      weekday: 'long', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function BookingManagePage() {
  const { token = '' } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid' | 'error'>('loading');
  const [retryNonce, setRetryNonce] = useState(0);
  const [data, setData] = useState<ManageData | null>(null);
  const [view, setView] = useState<'summary' | 'reschedule' | 'cancel-confirm' | 'cancelled' | 'past'>('summary');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    (async () => {
      try {
        const res = await fetchWithRetry(`${API}/api/public/bookings/manage/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.status === 401) { setStatus('invalid'); return; }
        if (!res.ok) { setStatus('error'); return; }
        const d = await res.json() as ManageData;
        if (cancelled) return;
        setData(d);
        // A booking already cancelled (and not by a reschedule) lands straight
        // on the honest end state rather than offering dead buttons.
        if (d.booking.phase === 'cancelled' || (d.booking.status !== 'scheduled' && !d.booking.superseded)) setView('cancelled');
        else if (d.booking.phase === 'past') setView('past');
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, retryNonce]);

  async function submitCancel() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/public/bookings/manage/${encodeURIComponent(token)}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string };
      if (!r.ok || !d.ok) throw new Error(d.error ?? 'Could not cancel — please try again.');
      setView('cancelled');
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const inner = (() => {
    if (status === 'loading') return <p style={muted}>Loading your booking…</p>;
    if (status === 'invalid') {
      return (
        <>
          <h1 style={h1}>This link has expired</h1>
          <p style={muted}>
            Manage links stay live for 60 days. Email us and we'll sort your
            booking out personally.
          </p>
        </>
      );
    }
    if (status === 'error' || !data) {
      return (
        <>
          <p style={muted}>We couldn't load your booking.</p>
          <button style={secondaryBtn} onClick={() => setRetryNonce((n) => n + 1)}>Try again</button>
        </>
      );
    }

    const b = data.booking;
    const hostFirst = b.hostName?.split(' ')[0] ?? 'us';

    const bookAgain = data.bookAgainUrl ? (
      <a href={data.bookAgainUrl} style={{ ...primaryBtn, display: 'inline-block', textDecoration: 'none' }}>
        Book a new time
      </a>
    ) : null;

    if (view === 'cancelled') {
      return (
        <>
          <h1 style={h1}>Your booking is cancelled</h1>
          <p style={muted}>
            Your call{b.hostName ? ` with ${hostFirst}` : ''} on {whenLabel(b.startIso, b.bookerTimezone)} was
            cancelled and the time released. Changed your mind? You're always welcome to book a new time.
          </p>
          {bookAgain}
        </>
      );
    }

    if (view === 'past') {
      return (
        <>
          <h1 style={h1}>This call has already happened</h1>
          <p style={muted}>
            Your call{b.hostName ? ` with ${hostFirst}` : ''} was on {whenLabel(b.startIso, b.bookerTimezone)}.
            {' '}If you'd like to talk again, you're very welcome to book a new time.
          </p>
          {bookAgain}
        </>
      );
    }

    if (b.phase === 'live') {
      return (
        <>
          <h1 style={h1}>Your call is on now</h1>
          <div style={summaryCard}>
            <p style={summaryWhen}>{whenLabel(b.startIso, b.bookerTimezone)}</p>
            {b.hostName && <p style={mutedTight}>{b.durationMinutes ?? 30} minutes with {b.hostName}</p>}
          </div>
          {b.joinUrl ? (
            <a href={b.joinUrl} style={{ ...primaryBtn, display: 'inline-block', textDecoration: 'none' }}>
              Join the call
            </a>
          ) : (
            <p style={mutedTight}>Your join link is in your confirmation email.</p>
          )}
        </>
      );
    }

    if (view === 'reschedule' && data.rebook) {
      return (
        <>
          <h1 style={h1}>Pick a new time</h1>
          <p style={mutedTight}>Currently: {whenLabel(b.startIso, b.bookerTimezone)}</p>
          <div style={pickerCard}>
            <NativeBookingPicker
              personSlug={data.rebook.personSlug}
              bookingKey={data.rebook.bookingKey}
              rebookToken={token}
              onFallback={() => setView('summary')}
            />
          </div>
          <button style={{ ...linkBtn, marginTop: 14 }} onClick={() => setView('summary')} type="button">
            ← back
          </button>
        </>
      );
    }

    if (view === 'cancel-confirm') {
      return (
        <>
          <h1 style={h1}>Cancel this booking?</h1>
          <p style={muted}>
            Your call with {hostFirst} on {whenLabel(b.startIso, b.bookerTimezone)} will be
            cancelled and the time released. If it's a timing problem, rescheduling
            keeps your spot with {hostFirst}.
          </p>
          {error && <p style={errorLine}>{error}</p>}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button style={dangerBtn} disabled={busy} onClick={submitCancel} type="button">
              {busy ? 'Cancelling…' : 'Yes, cancel it'}
            </button>
            <button style={secondaryBtn} onClick={() => setView('summary')} type="button">
              Keep my booking
            </button>
          </div>
        </>
      );
    }

    // summary
    return (
      <>
        <h1 style={h1}>{b.firstName ? `Hi ${b.firstName} — your` : 'Your'} booking</h1>
        <div style={summaryCard}>
          <p style={summaryWhen}>{whenLabel(b.startIso, b.bookerTimezone)}</p>
          {b.hostName && <p style={mutedTight}>{b.durationMinutes ?? 30} minutes with {b.hostName}</p>}
          {b.joinUrl && (
            <p style={{ margin: '10px 0 0' }}>
              <a href={b.joinUrl} style={joinLink}>Join link</a>
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {data.rebook ? (
            <button style={primaryBtn} onClick={() => setView('reschedule')} type="button">
              Pick a new time
            </button>
          ) : (
            <p style={mutedTight}>Need a different time? Reply to your confirmation email and we'll sort it.</p>
          )}
          <button style={secondaryBtn} onClick={() => setView('cancel-confirm')} type="button">
            Cancel this booking
          </button>
        </div>
      </>
    );
  })();

  return (
    <TakerShell>
      <div style={wrap}>{inner}</div>
    </TakerShell>
  );
}

// ---- styles ----

const wrap: CSSProperties = {
  maxWidth: 640,
  margin: '0 auto',
  padding: '48px 20px 80px',
};

const h1: CSSProperties = {
  margin: '0 0 14px',
  fontSize: 26,
  fontWeight: 800,
  color: '#0F172A',
  letterSpacing: '-0.02em',
};

const muted: CSSProperties = {
  margin: '0 0 20px',
  fontSize: 15,
  lineHeight: 1.65,
  color: '#475569',
};

const mutedTight: CSSProperties = {
  margin: '0 0 10px',
  fontSize: 14,
  color: '#64748B',
};

const summaryCard: CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: 14,
  padding: '18px 20px',
  margin: '0 0 22px',
};

const summaryWhen: CSSProperties = {
  margin: 0,
  fontSize: 17,
  fontWeight: 700,
  color: '#0F172A',
};

const joinLink: CSSProperties = {
  color: '#0F172A',
  fontWeight: 600,
};

const pickerCard: CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: 14,
  overflow: 'hidden',
};

const primaryBtn: CSSProperties = {
  padding: '12px 22px',
  borderRadius: 10,
  border: 'none',
  background: '#0F172A',
  color: '#fff',
  fontWeight: 700,
  fontSize: 15,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const secondaryBtn: CSSProperties = {
  padding: '12px 22px',
  borderRadius: 10,
  border: '1px solid #CBD5E1',
  background: '#fff',
  color: '#0F172A',
  fontWeight: 600,
  fontSize: 15,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const dangerBtn: CSSProperties = {
  ...secondaryBtn,
  border: '1px solid #FCA5A5',
  color: '#B91C1C',
};

const linkBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#475569',
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: 14,
  fontFamily: 'inherit',
};

const errorLine: CSSProperties = {
  margin: '0 0 12px',
  fontSize: 13.5,
  color: '#B91C1C',
};
